import { IOrderAuditTimelineEvent } from '../Services/api/audit/audit.interface'
import { OrderProductionStatusEnum, OrderStatusEnum } from '../Services/api/orders/orders.interface'
import { ServiceOrderStageEnum } from '../Services/api/service-orders/service-order.interface'
import { billingFlowLabels } from './billingFlow'
import { FormatMoney } from './formatMoney'
import { normalizeOrderStatusTitle } from './normalizeOrderStatus'
import { labelsOrderProductionStage } from './normalizeProductionStages'

const MAX_TEXT_LENGTH = 120

const ORDER_DISPLAY_FIELDS = new Set([
  'status',
  'sellerId',
  'productionStatus',
  'billingFlow',
  'billing_flow',
  'observations',
  'errorMessage',
  'totalValue',
  'discountValue',
  'originalValue',
  'dueDate',
  'nsPaymentGatewayName',
  'nsShippingCost',
  'cancelReason',
])

const PRODUCT_DISPLAY_FIELDS = new Set([
  'status',
  'totalQuantity',
  'orderQuantity',
  'finalPrice',
  'unitaryPrice',
  'volumetry',
  'artLink',
  'productionDueDate',
  'newProductionDueDate',
  'hcpCurrentFlowStatusName',
])

const PAYMENT_DISPLAY_FIELDS = new Set([
  'status',
  'gatewayName',
  'modality',
  'errorMessage',
])

const ORDER_FIELD_LABELS: Record<string, string> = {
  sellerId: 'Vendedor',
  status: 'Status do pedido',
  billingFlow: 'Fluxo de faturamento',
  billing_flow: 'Fluxo de faturamento',
  productionStatus: 'Status de produção',
  observations: 'Observações',
  errorMessage: 'Mensagem de erro',
  totalValue: 'Total',
  discountValue: 'Desconto',
  originalValue: 'Valor original',
  store: 'Loja',
  nsOrderId: 'Pedido NuvemShop',
  nsPaymentGatewayName: 'Gateway de pagamento',
  nsShippingCost: 'Frete',
  cancelReason: 'Motivo do cancelamento',
  dueDate: 'Data de vencimento',
}

const PRODUCT_FIELD_LABELS: Record<string, string> = {
  status: 'Etapa de produção',
  totalQuantity: 'Quantidade total',
  orderQuantity: 'Quantidade no pedido',
  finalPrice: 'Preço final',
  unitaryPrice: 'Preço unitário',
  volumetry: 'Volumetria',
  artLink: 'Arte',
  productionDueDate: 'Prazo de produção',
  newProductionDueDate: 'Novo prazo de produção',
  hcpCurrentFlowStatusName: 'Status do fluxo',
}

const PAYMENT_FIELD_LABELS: Record<string, string> = {
  status: 'Status do pagamento',
  gatewayName: 'Gateway',
  modality: 'Modalidade',
  errorMessage: 'Mensagem de erro',
}

const CUSTOMER_FIELD_LABELS: Record<string, string> = {
  name: 'Nome',
  email: 'E-mail',
  phone: 'Telefone',
  document: 'Documento',
  companyName: 'Razão social',
  tradeName: 'Nome fantasia',
}

const ADDRESS_FIELD_LABELS: Record<string, string> = {
  street: 'Rua',
  number: 'Número',
  locality: 'Bairro',
  city: 'Cidade',
  province: 'Estado',
  zipcode: 'CEP',
  name: 'Destinatário',
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  ORDER_CHANGE: 'Pedido',
  PRODUCT_CHANGE: 'Produto',
  STOCK_MOVEMENT: 'Estoque',
  PAYMENT_CHANGE: 'Pagamento',
  BILLING_CHANGE: 'Faturamento',
  SHIPPING_CHANGE: 'Envio',
  ADDRESS_CHANGE: 'Endereço',
  CUSTOMER_CHANGE: 'Cliente',
  SELLER_CHANGE: 'Vendedor',
}

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente',
  PROCESSING: 'Em processamento',
  SUCCESS: 'Processado com sucesso',
  ERROR: 'Erro no processamento',
}

const PRODUCTION_STATUS_LABELS: Record<OrderProductionStatusEnum, string> = {
  [OrderProductionStatusEnum.STAND_BY]: 'Aguardando produção',
  [OrderProductionStatusEnum.PRODUCTION]: 'Em produção',
  [OrderProductionStatusEnum.HALF_FINISHED]: 'Parcialmente produzido',
  [OrderProductionStatusEnum.FINISHED]: 'Produzido',
}

const ORDER_CREATE_SUMMARY_FIELDS = ['nsOrderId', 'store', 'status'] as const

const CUSTOMER_RELEVANT_FIELDS = new Set(Object.keys(CUSTOMER_FIELD_LABELS))
const ADDRESS_RELEVANT_FIELDS = new Set(Object.keys(ADDRESS_FIELD_LABELS))

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

function truncateText(value: string, maxLength = MAX_TEXT_LENGTH): string {
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength)}…`
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

function isInternalField(field: string): boolean {
  if (field.endsWith('Id') && field !== 'nsOrderId') return true
  if (field.includes('AlreadyAdded')) return true
  if (field.startsWith('hcp') && field !== 'hcpCurrentFlowStatusName') return true
  if (['omieCategoryId', 'hasShippingCost', 'nsPaymentGateway', 'type', 'updatedAt', 'createdAt', 'deletedAt'].includes(field)) {
    return true
  }

  return false
}

function getMetadataRecord(event: IOrderAuditTimelineEvent) {
  return (event.metadata ?? {}) as {
    changedFields?: string[]
    previousValues?: Record<string, unknown>
    newValues?: Record<string, unknown>
    notes?: string
    productName?: string
    stockName?: string
    howMany?: number
  }
}

function getWhitelistForEvent(event: IOrderAuditTimelineEvent): Set<string> | null {
  switch (event.eventType) {
    case 'ORDER_CHANGE':
      return ORDER_DISPLAY_FIELDS
    case 'PRODUCT_CHANGE':
      return PRODUCT_DISPLAY_FIELDS
    case 'PAYMENT_CHANGE':
      return PAYMENT_DISPLAY_FIELDS
    default:
      return null
  }
}

export function getDisplayableChangedFields(event: IOrderAuditTimelineEvent): string[] {
  const metadata = getMetadataRecord(event)
  const whitelist = getWhitelistForEvent(event)

  return (metadata.changedFields ?? []).filter(field => {
    if (isInternalField(field)) return false
    if (whitelist) return whitelist.has(field)
    return !isInternalField(field)
  })
}

function valuesAreEqual(field: string, previous?: unknown, next?: unknown, eventType?: string): boolean {
  if (isEmptyValue(previous) && isEmptyValue(next)) return true
  return formatFieldValue(field, previous, eventType) === formatFieldValue(field, next, eventType)
}

function formatFieldLabel(field: string, eventType?: string): string {
  if (eventType === 'PRODUCT_CHANGE') {
    return PRODUCT_FIELD_LABELS[field] || ORDER_FIELD_LABELS[field] || field
  }

  if (eventType === 'PAYMENT_CHANGE') {
    return PAYMENT_FIELD_LABELS[field] || field
  }

  if (eventType === 'CUSTOMER_CHANGE') {
    return CUSTOMER_FIELD_LABELS[field] || field
  }

  if (eventType === 'ADDRESS_CHANGE') {
    return ADDRESS_FIELD_LABELS[field] || field
  }

  return ORDER_FIELD_LABELS[field] || field
}

function formatMoneyValue(value: unknown): string | null {
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return null
  return FormatMoney(numeric)
}

function formatDateValue(value: unknown): string | null {
  if (isEmptyValue(value)) return null

  try {
    const date = new Date(String(value))
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleString('pt-BR')
  } catch {
    return null
  }
}

function formatFieldValue(field: string, value: unknown, eventType?: string): string {
  if (isEmptyValue(value)) {
    return 'Não informado'
  }

  if (field === 'status' && typeof value === 'string') {
    if (eventType === 'PAYMENT_CHANGE' && PAYMENT_STATUS_LABELS[value]) {
      return PAYMENT_STATUS_LABELS[value]
    }

    if (eventType === 'PRODUCT_CHANGE' && labelsOrderProductionStage[value as ServiceOrderStageEnum]) {
      return labelsOrderProductionStage[value as ServiceOrderStageEnum]
    }

    return normalizeOrderStatusTitle(value as OrderStatusEnum) || value
  }

  if ((field === 'billingFlow' || field === 'billing_flow') && typeof value === 'string') {
    return billingFlowLabels[value as keyof typeof billingFlowLabels] || value
  }

  if (field === 'productionStatus' && typeof value === 'string') {
    return PRODUCTION_STATUS_LABELS[value as OrderProductionStatusEnum] || value
  }

  if (['totalValue', 'discountValue', 'originalValue', 'finalPrice', 'unitaryPrice', 'nsShippingCost'].includes(field)) {
    return formatMoneyValue(value) || String(value)
  }

  if (field.endsWith('Date') || field.endsWith('At')) {
    return formatDateValue(value) || truncateText(String(value))
  }

  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não'
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (typeof value === 'string') {
    if (isUuidLike(value)) return 'Registro vinculado'
    return truncateText(value)
  }

  if (Array.isArray(value)) {
    return `${value.length} item(ns)`
  }

  return 'Dados técnicos atualizados'
}

function formatChangeLine(
  field: string,
  previousValues: Record<string, unknown> | undefined,
  newValues: Record<string, unknown> | undefined,
  eventType?: string,
): string | null {
  const previous = previousValues?.[field]
  const next = newValues?.[field]

  if (valuesAreEqual(field, previous, next, eventType)) {
    return null
  }

  const label = formatFieldLabel(field, eventType)
  const from = formatFieldValue(field, previous, eventType)
  const to = formatFieldValue(field, next, eventType)

  if (field === 'sellerId') {
    if (isEmptyValue(previous) && !isEmptyValue(next)) return `${label} atribuído`
    if (!isEmptyValue(previous) && isEmptyValue(next)) return `${label} removido`
    return `${label} alterado`
  }

  return `${label}: ${from} → ${to}`
}

function getTitleCoveredFields(event: IOrderAuditTimelineEvent): Set<string> {
  const fields = getDisplayableChangedFields(event)
  const covered = new Set<string>()

  if (fields.length === 1) {
    covered.add(fields[0])
    return covered
  }

  if (fields.includes('status')) {
    covered.add('status')
  }

  if (fields.includes('sellerId')) {
    covered.add('sellerId')
  }

  if (fields.includes('productionStatus') && fields.length <= 2) {
    covered.add('productionStatus')
  }

  if (event.eventType === 'PRODUCT_CHANGE' && fields.includes('status')) {
    covered.add('status')
  }

  return covered
}

function isGenericTitle(title: string): boolean {
  return /atualizado$/i.test(title) || title === 'Registro criado'
}

function formatCreateSummary(event: IOrderAuditTimelineEvent): string[] {
  const metadata = getMetadataRecord(event)
  const newValues = metadata.newValues ?? {}

  if (event.eventType === 'ORDER_CHANGE') {
    return ORDER_CREATE_SUMMARY_FIELDS
      .map(field => {
        const value = newValues[field]
        if (isEmptyValue(value)) return null
        return `${formatFieldLabel(field, event.eventType)}: ${formatFieldValue(field, value, event.eventType)}`
      })
      .filter((line): line is string => Boolean(line))
  }

  return []
}

export function isAuditEventVisible(event: IOrderAuditTimelineEvent): boolean {
  if (event.eventType === 'PRODUCT_CHANGE' && event.action === 'CREATE') {
    return false
  }

  if (event.eventType === 'CUSTOMER_CHANGE' || event.eventType === 'SELLER_CHANGE') {
    return false
  }

  if (event.eventType === 'STOCK_MOVEMENT') {
    return true
  }

  if (event.action === 'CREATE') {
    return event.eventType === 'ORDER_CHANGE' || event.eventType === 'PAYMENT_CHANGE' || event.eventType === 'BILLING_CHANGE'
  }

  if (event.action === 'DELETE') {
    return event.eventType !== 'PRODUCT_CHANGE'
  }

  if (event.action !== 'UPDATE') {
    return true
  }

  if (event.eventType === 'ADDRESS_CHANGE') {
    const metadata = getMetadataRecord(event)
    return (metadata.changedFields ?? []).some(field => ADDRESS_RELEVANT_FIELDS.has(field))
  }

  return getDisplayableChangedFields(event).length > 0
}

function getEventDedupKey(event: IOrderAuditTimelineEvent): string {
  const title = formatAuditEventTitle(event)
  const fields = getDisplayableChangedFields(event).join('|')

  if (event.eventType === 'STOCK_MOVEMENT') {
    return `stock|${event.timestamp}|${title}`
  }

  return `${event.eventType}|${event.action}|${event.timestamp}|${event.entityId ?? ''}|${title}|${fields}`
}

export function prepareAuditEventsForDisplay(events: IOrderAuditTimelineEvent[]): IOrderAuditTimelineEvent[] {
  const seen = new Set<string>()

  return events
    .filter(isAuditEventVisible)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .filter(event => {
      const key = getEventDedupKey(event)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

export function formatAuditEventCategory(event: IOrderAuditTimelineEvent): string {
  return EVENT_TYPE_LABELS[event.eventType] || 'Evento'
}

export function formatAuditEventTitle(event: IOrderAuditTimelineEvent): string {
  const metadata = getMetadataRecord(event)
  const changedFields = getDisplayableChangedFields(event)

  if (event.action === 'CREATE') {
    switch (event.eventType) {
      case 'ORDER_CHANGE':
        return 'Pedido registrado no Hub'
      case 'PAYMENT_CHANGE':
        return 'Pagamento registrado'
      case 'BILLING_CHANGE':
        return 'Faturamento iniciado no Omie'
      case 'STOCK_MOVEMENT':
        return simplifyStockDescription(event.description)
      default:
        return event.description || 'Registro criado'
    }
  }

  if (event.eventType === 'STOCK_MOVEMENT') {
    return simplifyStockDescription(event.description)
  }

  if (changedFields.length === 1 && changedFields[0] === 'sellerId') {
    const previous = metadata.previousValues?.sellerId
    const next = metadata.newValues?.sellerId

    if (isEmptyValue(previous) && !isEmptyValue(next)) return 'Vendedor atribuído ao pedido'
    if (!isEmptyValue(previous) && isEmptyValue(next)) return 'Vendedor removido do pedido'
    return 'Vendedor alterado no pedido'
  }

  if (changedFields.includes('status') && changedFields.length === 1) {
    const from = formatFieldValue('status', metadata.previousValues?.status, event.eventType)
    const to = formatFieldValue('status', metadata.newValues?.status, event.eventType)

    if (event.eventType === 'PRODUCT_CHANGE') {
      return `Etapa de produção: ${from} → ${to}`
    }

    if (event.eventType === 'PAYMENT_CHANGE') {
      return `Pagamento: ${from} → ${to}`
    }

    return `Status: ${from} → ${to}`
  }

  if (changedFields.length === 1 && (changedFields[0] === 'billingFlow' || changedFields[0] === 'billing_flow')) {
    const field = changedFields[0]
    const from = formatFieldValue(field, metadata.previousValues?.[field], event.eventType)
    const to = formatFieldValue(field, metadata.newValues?.[field], event.eventType)
    return `Fluxo de faturamento: ${from} → ${to}`
  }

  if (changedFields.length === 1 && changedFields[0] === 'productionStatus') {
    const from = formatFieldValue('productionStatus', metadata.previousValues?.productionStatus, event.eventType)
    const to = formatFieldValue('productionStatus', metadata.newValues?.productionStatus, event.eventType)
    return `Produção: ${from} → ${to}`
  }

  if (changedFields.length === 1 && changedFields[0] === 'hcpCurrentFlowStatusName') {
    const from = formatFieldValue('hcpCurrentFlowStatusName', metadata.previousValues?.hcpCurrentFlowStatusName, event.eventType)
    const to = formatFieldValue('hcpCurrentFlowStatusName', metadata.newValues?.hcpCurrentFlowStatusName, event.eventType)
    return `Fluxo do produto: ${from} → ${to}`
  }

  if (event.eventType === 'PAYMENT_CHANGE' && changedFields.includes('status')) {
    const from = formatFieldValue('status', metadata.previousValues?.status, event.eventType)
    const to = formatFieldValue('status', metadata.newValues?.status, event.eventType)
    return `Pagamento: ${from} → ${to}`
  }

  const category = formatAuditEventCategory(event)
  return `${category} atualizado`
}

function simplifyStockDescription(description?: string): string {
  if (!description) return 'Movimentação de estoque'

  return description
    .replace(/\s+adicionado do estoque\s+/i, ' reservado em ')
    .replace(/\s+removido do estoque\s+/i, ' liberado em ')
}

export function formatAuditEventDetails(event: IOrderAuditTimelineEvent): string[] {
  if (event.eventType === 'STOCK_MOVEMENT') {
    return []
  }

  const metadata = getMetadataRecord(event)
  const title = formatAuditEventTitle(event)
  const genericTitle = isGenericTitle(title)
  const titleCoveredFields = getTitleCoveredFields(event)
  const details: string[] = []

  if (event.action === 'CREATE') {
    details.push(...formatCreateSummary(event))
    return details
  }

  if (event.action === 'UPDATE') {
    getDisplayableChangedFields(event).forEach(field => {
      if (!genericTitle && titleCoveredFields.has(field)) {
        return
      }

      const line = formatChangeLine(
        field,
        metadata.previousValues,
        metadata.newValues,
        event.eventType,
      )

      if (line) {
        details.push(line)
      }
    })
  }

  if (metadata.notes) {
    details.push(truncateText(String(metadata.notes), 200))
  }

  return details
}
